// ── GameTable — V2 two-row moneyline table ──────────────────────────
// Per-team Yes/No price pills, global Yes/No toggle in the heading.
// Selecting either team row expands the game inline into paired full-width
// ladders: away book, both team rows together, then home book. The right-side
// detail panel keeps the deeper streaming view; this is the quick paired-market
// view.

import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/infra/utils';
import { GameCountdown, LiveBadge, NearEventStartPulse } from './GameTimeIndicators';
import { LiquidityBar } from '@/components/atoms/LiquidityBar';
import { Th, Td } from '@/components/atoms/TableCells';
import { SortableTh, type SortDirection } from '@/components/atoms/SortableTh';
import { formatCompactCurrency } from '@/lib/infra/formatters';
import { formatGameDateLabel, getTimeAgo } from '@/lib/infra/dateUtils';
import { roundLabel } from '@/services/hydraAnalytics';
import { toHydraGameKey } from '@/services/analyticsGameKey';
import type { OddsStreamGameState } from '@/services/oddsStream';
import { useGameStateFor } from '@/stores/gameStatesStore';
import { useConsensusFor } from '@/stores/marketStore';
import { formatGameStatus, isConfirmedFinal } from '@/lib/sport/periodLabel';
import { queueTone } from '@/components/cards/orderCardModel';
import {
  bookDepthVelocity,
  depthDeltaTone,
  depthKey,
  formatSignedVolume,
} from '@/components/cards/bookDepthVelocity';
import type {
  UserPositionSlice,
  UserOrderSlice,
} from '@/hooks/useUserKalshiPOS';
import type { GameRow, MarketType, MarketSide, PriceMode, TeamSide, BookLevel, VenueBook } from './types';
import { americanOdds } from './tradeModalBook';
import { useDisplaySettings, type DashboardSortKey } from '@/stores/displaySettings';
import { useFeeFor } from '@/services/hydraFees';
import { useFlashOnChange, FLASH_CLASS } from '@/hooks/ui/useFlash';
import {
  LADDER_LEVELS,
  bidIsReal,
  pillPriceCents,
  bestBidDepthForSide,
  effectivePriceForOdds,
  formatVolume,
  queueDepth,
  orderTooltip,
  type BidRowOrder,
  type FeePerspective,
} from './ladder/helpers';
import { PricePill } from './ladder/PricePill';
import { PosRing } from './ladder/PosRing';
import { useMakerRebateActive } from '@/hooks/useMakerRebate';
import { makerRebatePct } from '@/lib/pricing/makerRebate';

// ── Props ────────────────────────────────────────────────────────────

interface GameTableProps {
  games: GameRow[];
  titleLabel?: string;
  seriesTicker: string;
  marketType: MarketType;
  marketSide: MarketSide;
  priceMode: PriceMode;
  expandedGameKey: string | null;
  expandedTeamSide: TeamSide | null;
  onToggleExpand: (key: string, side: TeamSide) => void;
  onTradeClick?: (
    gameKey: string,
    side: 'away' | 'home',
    venue: 'kalshi' | 'polymarket',
    opts?: { priceCents?: number; initialOrderSide?: 'yes' | 'no'; postOnly?: boolean },
  ) => void;
  /** Per-Kalshi-ticker POS overlay for the logged-in user. Empty Maps until
   *  the first /api/trader/positions + /api/kalshi/orders responses arrive. */
  positionByTicker: Map<string, UserPositionSlice>;
  ordersByTicker: Map<string, UserOrderSlice[]>;
  /** Hydra canonical gameKeys (`date|TEAM_A|TEAM_B`, alpha-sorted) the user
   *  has any position in. Used by the archived skinny row to draw a left-
   *  border highlight so finished games the user actually traded stand
   *  out in the post-game scan. Default empty set. */
  positionGameKeys?: ReadonlySet<string>;
  /** True while the archived (analytics-backed) fetch is in flight for
   *  the selected date. Drives skeleton placeholder rows so the user
   *  sees something is loading instead of a blank gap below the live
   *  rows. */
  archivedLoading?: boolean;
}

// ── Helpers ──────────────────────────────────────────────────────────

const MARKET_TYPE_LABELS: Record<MarketType, string> = {
  moneyline: 'Moneylines',
  spread: 'Spreads',
  total: 'Totals',
  'spread-total': 'Spread/Total',
  series: 'Futures',
};
const VELOCITY_ALERT_DELTA = 50_000;

function depthVelocityDelta(book: VenueBook | null | undefined, side: MarketSide): number | null {
  const ticker = book?.marketTicker;
  return ticker ? bookDepthVelocity(depthKey(ticker, side)).m1.depthDelta : null;
}

function velocityMagnitude(...deltas: Array<number | null>): number {
  let max = 0;
  for (const delta of deltas) {
    if (delta != null) max = Math.max(max, Math.abs(delta));
  }
  return max;
}

// ── GameTable ────────────────────────────────────────────────────────

export function GameTable({
  games,
  titleLabel,
  seriesTicker,
  marketType,
  marketSide,
  priceMode,
  expandedGameKey,
  expandedTeamSide,
  onToggleExpand,
  onTradeClick,
  positionByTicker,
  ordersByTicker,
  positionGameKeys,
  archivedLoading,
}: GameTableProps) {
  // Initial sort is sourced from the user's persisted preference in
  // displaySettings; factory default is `{ key: null, dir: 'asc' }` which
  // preserves the prior data-natural order (no applied sort). Read at mount
  // time only — header clicks during the session don't propagate back into
  // the setting, matching the convention used by defaultSport / defaultPriceMode
  // / defaultExpiryMode. Per-key direction defaults on first click live in
  // handleSort below; date/team are asc-first, queue is desc-first (a
  // market-maker scanning for big stacks at the front of the book wants
  // the deepest queues at the top).
  const [sortKey, setSortKey] = useState<string | null>(
    () => useDisplaySettings.getState().defaultDashboardSort.key,
  );
  const [sortDir, setSortDir] = useState<SortDirection>(
    () => useDisplaySettings.getState().defaultDashboardSort.dir,
  );
  // Read the persisted default reactively (not via lazy useState) so the
  // pin-icon's filled/outlined state updates when the trader toggles the
  // pin from the dashboard, without forcing a remount.
  const persistedSort = useDisplaySettings((s) => s.defaultDashboardSort);
  const setDefaultDashboardSort = useDisplaySettings(
    (s) => s.setDefaultDashboardSort,
  );

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'queue' || key === 'velocity' ? 'desc' : 'asc');
    }
  };

  // Pin / unpin the current sort as the persisted default. If the active
  // sort already matches the saved default, click clears the default (back
  // to factory "unsorted"). Otherwise click saves the active sort+dir as
  // the new default — the trader gets the same sort the next time they
  // open the dashboard, without opening Settings.
  const isCurrentSortPinned =
    sortKey !== null &&
    persistedSort.key === sortKey &&
    persistedSort.dir === sortDir;
  const handlePinSort = () => {
    if (sortKey === null) return; // no sort to pin
    if (isCurrentSortPinned) {
      setDefaultDashboardSort({ key: null, dir: 'asc' });
    } else {
      setDefaultDashboardSort({
        key: sortKey as DashboardSortKey,
        dir: sortDir === 'asc' ? 'asc' : 'desc',
      });
    }
  };

  // Apply the active sort. The previous version held sortKey/sortDir state
  // but never used it — header clicks just toggled the indicator arrow while
  // the row order stayed at whatever ValueDashboard handed us. useMemo so
  // resorting only happens when the inputs actually change; archived rows
  // always sink to the bottom regardless of sort key so finished games
  // don't pollute the live scan.
  const sortedGames = useMemo(() => {
    if (sortKey == null) return games;
    const dirMult = sortDir === 'asc' ? 1 : -1;
    const gameQueueDepth = (g: GameRow): number => {
      const a = bestBidDepthForSide(g.kalshi?.away, marketSide) ?? 0;
      const h = bestBidDepthForSide(g.kalshi?.home, marketSide) ?? 0;
      return Math.max(a, h);
    };
    const gameVelocity = (g: GameRow): number =>
      velocityMagnitude(
        depthVelocityDelta(g.kalshi?.away, marketSide),
        depthVelocityDelta(g.kalshi?.home, marketSide),
        depthVelocityDelta(g.polymarket?.away, marketSide),
        depthVelocityDelta(g.polymarket?.home, marketSide),
      );
    const copy = [...games];
    copy.sort((g1, g2) => {
      if (g1.archived && !g2.archived) return 1;
      if (!g1.archived && g2.archived) return -1;
      if (sortKey === 'date') {
        const t1 = g1.startTimeMs ?? Number.POSITIVE_INFINITY;
        const t2 = g2.startTimeMs ?? Number.POSITIVE_INFINITY;
        return (t1 - t2) * dirMult;
      }
      if (sortKey === 'team') {
        const cmp =
          g1.awayCode.localeCompare(g2.awayCode) ||
          g1.homeCode.localeCompare(g2.homeCode);
        return cmp * dirMult;
      }
      if (sortKey === 'queue') {
        return (gameQueueDepth(g1) - gameQueueDepth(g2)) * dirMult;
      }
      if (sortKey === 'velocity') {
        return (gameVelocity(g1) - gameVelocity(g2)) * dirMult;
      }
      return 0;
    });
    return copy;
  }, [games, sortKey, sortDir, marketSide]);

  // Score/Time column label mirrors what the cells actually render: pre-game
  // rows show kickoff time + countdown, started (live/final) rows show the
  // score, archived rows a dash (excluded). The old `.some(started)` rule
  // flipped the whole header to "Score" while every visible row still showed
  // a countdown, because one afternoon final poisons the evening slate.
  const liveRows = games.filter((g) => !g.archived);
  const startedCount = liveRows.filter(
    (g) => g.gameState === 'live' || g.gameState === 'final',
  ).length;
  const scoreColLabel =
    liveRows.length === 0 || startedCount === liveRows.length
      ? 'Score'
      : startedCount === 0
        ? 'Time'
        : 'Time / Score';

  return (
    <div className="bg-card border-border rounded-lg border">
      {/* Title bar — sport + market type. YES/NO + price-mode toggles live on
          the toolbar's row 2 above. */}
      <div className="border-border flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-3">
          <h2 className="text-foreground text-lg font-semibold">
            {titleLabel ?? `${games[0]?.sport?.toUpperCase() ?? ''} ${MARKET_TYPE_LABELS[marketType]}`}
          </h2>
          {seriesTicker && (
            <>
              <span className="text-muted-foreground text-xs">&mdash;</span>
              <span className="text-muted-foreground font-mono text-xs">{seriesTicker}</span>
            </>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse border-spacing-0">
          <thead className="bg-muted/50 border-border border-b">
            <tr>
              <SortableTh
                className="w-28"
                sortKey="date"
                activeSortKey={sortKey}
                activeSortDir={sortDir}
                onSort={handleSort}
                isPinned={isCurrentSortPinned && sortKey === 'date'}
                onPin={sortKey === 'date' ? handlePinSort : undefined}
              >
                Date
              </SortableTh>
              <SortableTh
                className="w-48"
                sortKey="team"
                activeSortKey={sortKey}
                activeSortDir={sortDir}
                onSort={handleSort}
                isPinned={isCurrentSortPinned && sortKey === 'team'}
                onPin={sortKey === 'team' ? handlePinSort : undefined}
              >
                Team
              </SortableTh>
              <Th
                className="w-28 text-center"
                title="Kickoff time + countdown until the game starts, then the live/final score."
              >
                {scoreColLabel}
              </Th>
              <Th
                className="w-16 text-center"
                title="Unabated consensus win probability (pre-game / live); blank once the game is final."
              >
                Odds
              </Th>
              <Th className="w-32 text-center">Kalshi</Th>
              <Th className="w-16 text-right" title="Total BID/ASK contracts">K Vol</Th>
              <Th className="w-24 text-right" title="Total $ resting in book (bids + asks)">K Liq</Th>
              <SortableTh
                className="w-16 text-right"
                sortKey="queue"
                activeSortKey={sortKey}
                activeSortDir={sortDir}
                onSort={handleSort}
                isPinned={isCurrentSortPinned && sortKey === 'queue'}
                onPin={sortKey === 'queue' ? handlePinSort : undefined}
                title={`Best-bid depth on the active ${marketSide.toUpperCase()} side. Green <50k / Yellow 50–300k / Red ≥300k.`}
              >
                K Q
              </SortableTh>
              <SortableTh
                className="w-16 text-right"
                sortKey="velocity"
                activeSortKey={sortKey}
                activeSortDir={sortDir}
                onSort={handleSort}
                isPinned={isCurrentSortPinned && sortKey === 'velocity'}
                onPin={sortKey === 'velocity' ? handlePinSort : undefined}
                title="Kalshi best-bid depth change over the last 1m on the active side. Positive = depth building; negative = draining. Empty until 1m of samples accumulates."
              >
                K Δ/m
              </SortableTh>
              <Th className="w-32 text-center">Poly</Th>
              <Th className="w-16 text-right" title="Total BID/ASK contracts">P Vol</Th>
              <Th className="w-24 text-right" title="Total $ resting in book (bids + asks)">P Liq</Th>
              <Th
                className="w-16 text-right"
                title="Polymarket best-bid depth change over the last 1m on the active side. Same semantic as K Δ/m — pair them to spot cross-exchange sweep imbalance."
              >
                P Δ/m
              </Th>
            </tr>
          </thead>
          <tbody>
            {sortedGames.map((game, gameIndex) =>
              game.archived ? (
                <ArchivedRow
                  key={game.key}
                  game={game}
                  hasPosition={positionGameKeys?.has(game.key) ?? false}
                />
              ) : (
                <GameRows
                  key={game.key}
                  game={game}
                  gameIndex={gameIndex}
                  marketSide={marketSide}
                  priceMode={priceMode}
                  expandedTeamSide={expandedGameKey === game.key ? expandedTeamSide : null}
                  onToggleExpand={onToggleExpand}
                  onTradeClick={onTradeClick}
                  positionByTicker={positionByTicker}
                  ordersByTicker={ordersByTicker}
                  showScores
                />
              ),
            )}
            {archivedLoading && <ArchivedSkeletonRow />}
          </tbody>
        </table>
      </div>

      {/* Empty state — suppressed while archived analytics is in flight
          so the skeleton row in the tbody gets to be the loading
          signal instead of a competing "No games available" message. */}
      {games.length === 0 && !archivedLoading && (
        <div className="text-muted-foreground p-8 text-center">No games available</div>
      )}
    </div>
  );
}

// ── ArchivedRow — single skinny row for closed games sourced from
//    /api/analytics/games. Date and each team code are deep-links into
//    the per-game detail page; team click seeds GameDetail's selected
//    team via ?team=. Only K Vol / P Vol carry data — every other
//    column dashes since the analytics summary endpoint doesn't expose
//    closing prices/liquidity (tracked in
//    [[project_hydra_ask_analytics_games_with_prices]]).

interface ArchivedRowProps {
  game: GameRow;
  /** True when the user holds any position in this game — drives the
   *  left-border highlight so finished games the user actually traded
   *  pop in the post-game scan. */
  hasPosition: boolean;
}

function ArchivedRow({ game, hasPosition }: ArchivedRowProps) {
  const archived = game.archived;
  if (!archived) return null;
  const baseHref = `/game/${game.date}/${game.awayCode}/${game.homeCode}`;
  const linkClass =
    'text-foreground/80 hover:text-foreground hover:underline focus:outline-none focus-visible:underline';
  const dashCell = <span className="text-muted-foreground/40">—</span>;
  return (
    <tr
      className={cn(
        'bg-muted/10 text-muted-foreground border-t border-border/40 text-xs',
        hasPosition && 'border-l-2 border-l-cyan-400/60',
      )}
    >
      <Td className="whitespace-nowrap font-mono">
        <Link to={baseHref} className={linkClass} title="Open game detail (auto-picks winner)">
          {formatGameDateLabel(game.date)}
        </Link>
      </Td>
      <Td className="whitespace-nowrap">
        <Link
          to={`${baseHref}?team=${encodeURIComponent(game.awayCode)}`}
         
          className={linkClass}
          title={`Open game detail from ${game.awayCode}'s perspective`}
        >
          {game.awayCode}
        </Link>
        <span className="text-muted-foreground/40 px-1">@</span>
        <Link
          to={`${baseHref}?team=${encodeURIComponent(game.homeCode)}`}
         
          className={linkClass}
          title={`Open game detail from ${game.homeCode}'s perspective`}
        >
          {game.homeCode}
        </Link>
        <span className="text-muted-foreground/60 ml-2 rounded bg-muted/40 px-1 py-px text-[9px] uppercase tracking-wider">
          Final
        </span>
      </Td>
      {/* Score placeholder — archived rows have no per-team score. */}
      <Td className="text-left">{dashCell}</Td>
      {/* Odds placeholder — archived rows are one-line "AWAY @ HOME"
          summaries with no per-team consensus split. */}
      <Td className="text-right">{dashCell}</Td>
      <Td className="text-center">{dashCell}</Td>
      <Td className="text-right font-mono">
        {archived.kalshiVolume > 0 ? formatVolume(archived.kalshiVolume) : dashCell}
      </Td>
      <Td className="text-right">{dashCell}</Td>
      <Td className="text-right">{dashCell}</Td>
      {/* K Δ/m placeholder — archived rows have no live book to sample. */}
      <Td className="text-right">{dashCell}</Td>
      <Td className="text-center">{dashCell}</Td>
      <Td className="text-right font-mono">
        {archived.polyVolume > 0 ? formatVolume(archived.polyVolume) : dashCell}
      </Td>
      <Td className="text-right">{dashCell}</Td>
      {/* P Δ/m placeholder. */}
      <Td className="text-right">{dashCell}</Td>
    </tr>
  );
}

// Placeholder row shown while the analytics-backed archived fetch is in
// flight. Same column layout as ArchivedRow so the layout doesn't jump
// when real rows replace it. Pulse animation hints at "loading".
function ArchivedSkeletonRow() {
  const bar = (w: string) => (
    <span
      className={`inline-block h-2 ${w} animate-pulse rounded bg-muted-foreground/20`}
    />
  );
  return (
    <tr className="bg-muted/10 text-muted-foreground border-t border-border/40 text-xs">
      <Td className="whitespace-nowrap font-mono">{bar('w-10')}</Td>
      <Td className="whitespace-nowrap">{bar('w-24')}</Td>
      <Td className="text-left">{bar('w-10')}</Td>{/* Score */}
      <Td className="text-right">{bar('w-10')}</Td>{/* Odds */}
      <Td className="text-center">{bar('w-4')}</Td>
      <Td className="text-right">{bar('w-10')}</Td>
      <Td className="text-right">{bar('w-4')}</Td>
      <Td className="text-right">{bar('w-4')}</Td>
      <Td className="text-right">{bar('w-4')}</Td>{/* K Δ/m */}
      <Td className="text-center">{bar('w-4')}</Td>
      <Td className="text-right">{bar('w-10')}</Td>
      <Td className="text-right">{bar('w-4')}</Td>
      <Td className="text-right">{bar('w-4')}</Td>{/* P Δ/m */}
    </tr>
  );
}

/** Rank-based consensus tone for the Odds column. `value` is this row's
 *  rounded fair-prob; `all` is every outcome's rounded fair-prob for the
 *  same game (2 entries binary, 3 with a soccer Draw). Highest renders
 *  green and the LOWEST blue — the same green-leader/blue-trailer read
 *  as the GameDetail title and chart — while the middle (3-way only)
 *  and any tied values render grey. */
function consensusRankTone(value: number, all: number[]): string {
  if (all.length === 0) return 'text-muted-foreground';
  if (all.filter((v) => v === value).length >= 2) return 'text-muted-foreground';
  const distinct = [...new Set(all)].sort((a, b) => b - a);
  const rank = distinct.indexOf(value);
  if (rank === 0) return 'text-positive';
  if (rank === distinct.length - 1) return 'text-blue-400';
  return 'text-muted-foreground';
}

// ── GameRows — team rows + paired ladders for the expanded game ──────

export interface GameRowsProps {
  game: GameRow;
  gameIndex: number;
  marketSide: MarketSide;
  priceMode: PriceMode;
  /** null when no team in this game is expanded */
  expandedTeamSide: TeamSide | null;
  onToggleExpand: (key: string, side: TeamSide) => void;
  onTradeClick?: (
    gameKey: string,
    side: 'away' | 'home',
    venue: 'kalshi' | 'polymarket',
    opts?: { priceCents?: number; initialOrderSide?: 'yes' | 'no'; postOnly?: boolean },
  ) => void;
  positionByTicker: Map<string, UserPositionSlice>;
  ordersByTicker: Map<string, UserOrderSlice[]>;
  /** Drop the leading Date column from the row + ladder. Consumers like
   *  GameDetail show the date in the page header and don't need it
   *  repeated in every table row. Caller is responsible for matching
   *  the column-count in their own <thead>. */
  hideDateColumn?: boolean;
  /** Drop the Score/Time column (GameDetail's Game Lines table). Caller
   *  matches the column-count in its own <thead>. */
  hideScoreColumn?: boolean;
  /** Drop the Odds (consensus %) column — in the ladder rows this is the
   *  user-orders chip column, also dropped. Game Lines table. */
  hideOddsColumn?: boolean;
  /** Render ONLY this outcome's row + ladder instead of the away/home pair.
   *  For non-binary period bets (the 1H winner's Tie, single spread/total
   *  lines) that don't fit the two-team model. */
  singleSide?: TeamSide;
  /** Max-width Tailwind class for the team/bet label. Default 'max-w-[150px]'
   *  (dashboard). Game Lines passes a wider value because its labels are full
   *  sentences ("NYK wins by over 7.5") that the expanded selection box would
   *  otherwise truncate. */
  labelMaxWidthClass?: string;
  /** Suppress the blue selection box around the expanded game. GameDetail
   *  only ever shows a single moneyline, so the box is redundant noise
   *  there — the darker selected tint is kept, just the border is dropped. */
  hideSelectionBox?: boolean;
  /** Render the Unabated live score on the right edge of the Team cell
   *  for live + final games. Caller is responsible for mounting
   *  `useGameStatesPoll(...)` upstream so `gameStatesStore` has fresh
   *  data; GameRows just reads from the store. */
  showScores?: boolean;
  /** Fresh game state from the per-game WS odds stream (GameDetail). When
   *  provided it takes precedence over the analytics-REST gameStatesStore
   *  lookup, so the moneyline score tracks the live header instead of the
   *  laggier poll. */
  gameStateOverride?: OddsStreamGameState | null;
  /** Caller-determined "this market has resolved" flag (e.g. GameDetail's
   *  Game Lines table checks marketStore status per ticker). Dims the row
   *  and renders a SETTLED chip beside the bet label. Independent of
   *  `game.gameState === 'final'` on purpose: period synth rows never
   *  carry a final gameState (synthesize.ts), and the FINAL chip's home
   *  (the Date column) is hidden on those surfaces. */
  settled?: boolean;
  /** Marks a singleSide row that visually CONTINUES the previous GameRows
   *  group — the soccer Draw row under its away/home pair, the 1H Tie under
   *  the 1H moneyline pair. The row renders as a non-first row: no thick
   *  game-boundary top border, no repeated date / kickoff time, and (since
   *  zebra striping keys off gameIndex) the caller passes the SAME gameIndex
   *  as the group being continued so all three outcomes share one
   *  background. */
  continuation?: boolean;
  /** True when ANOTHER GameRows instance in this game's visual group is the
   *  expanded one (the Draw/Tie continuation when the pair is open, or the
   *  pair when the Draw/Tie is open). Applies the selected treatment — dark
   *  tint, blue side rails, zebra/boundary suppression — WITHOUT expanding
   *  this instance's own ladders, so the blue box wraps the whole 3-way
   *  market instead of just the clicked outcome's rows. */
  groupSelected?: boolean;
  /** The selection box continues below this instance (a Draw/Tie
   *  continuation row follows in the same group): leave the box open at the
   *  bottom — the continuation row (or its ladder) carries the bottom rail. */
  groupExtendsBelow?: boolean;
  /** This row IS the 3-way Draw outcome. The Odds column reads the game
   *  consensus' drawFairProbCents, and the per-team score cell stays blank —
   *  the synth's away slot holds the DRAW market, so the away team's fair
   *  prob / live goals would render on the wrong row otherwise. */
  oddsOutcome?: 'draw';
}

export function GameRows({
  game,
  gameIndex,
  marketSide,
  priceMode,
  expandedTeamSide,
  onToggleExpand,
  onTradeClick,
  positionByTicker,
  ordersByTicker,
  hideDateColumn = false,
  hideScoreColumn = false,
  hideOddsColumn = false,
  singleSide,
  labelMaxWidthClass = 'max-w-[150px]',
  hideSelectionBox = false,
  showScores = false,
  gameStateOverride = null,
  settled = false,
  continuation = false,
  groupSelected = false,
  groupExtendsBelow = false,
  oddsOutcome,
}: GameRowsProps) {
  // Maker-fee rebate fraction (0..1) for this game's maker-perspective odds.
  // 0 unless the org is enrolled in Kalshi's MM Program AND the MM Rebate
  // toggle is on. Live games rebate 100% of the maker fee, pregame 50%.
  // Same fraction for every row in the group (one game) and both venues —
  // Polymarket has no fee schedule so it's a no-op there.
  const makerRebateActive = useMakerRebateActive();
  const makerRebateFraction = makerRebateActive
    ? makerRebatePct({ isLive: game.gameState === 'live' })
    : 0;
  // Pull Unabated state for this game when scores are wanted. The
  // dashboard's poll keeps the store fresh; here we just consume.
  // `useGameStateFor` returns null when the gameKey isn't in the
  // store yet — caller's showScores=false code path doesn't run the
  // lookup at all.
  // Multi-outcome synth keys carry a `|multi|<code>` suffix — strip it so
  // the Draw row's consensus lookup resolves to the game (binary keys pass
  // through untouched).
  const hydraGameKey = showScores ? toHydraGameKey(game.key.split('|multi|')[0]) : null;
  const storeGameState = useGameStateFor(hydraGameKey);
  // Prefer the per-game WS odds-stream state (GameDetail) when present — it
  // updates in real time; the analytics-REST store lags. Falls back to the
  // store for the dashboard (no override) and during the WS connect window.
  const liveGameState = gameStateOverride ?? storeGameState;
  // Canonical key for the WS consensus channel — hydra's consensus event
  // ships `canonicalGameKey` as `${sport}|${date}|${A}|${B}` (matches the
  // shape on `/api/v1/markets`). `toHydraGameKey` returns the unprefixed
  // `${date}|${A}|${B}` for the gameStates poll; prefix sport here so the
  // lookup matches what `ingestConsensus` cached from the WS frame.
  const consensusKey = hydraGameKey ? `${game.sport.toLowerCase()}|${hydraGameKey}` : null;
  const liveConsensus = useConsensusFor(consensusKey);
  const showLiveScore =
    showScores &&
    liveGameState != null &&
    (liveGameState.statusId === 2 || liveGameState.statusId === 3);
  const awayScoreDisplay = showLiveScore ? liveGameState.awayScore : null;
  const homeScoreDisplay = showLiveScore ? liveGameState.homeScore : null;
  // Period status replaces the scheduled event start time in the date column
  // once the game is actually live (statusId === 2). Source comes from
  // the same gameStates poll the score uses; renders null pre-game and
  // for finals (those get their FINAL chip in the first line instead).
  const liveStatusText =
    showScores && liveGameState && liveGameState.statusId === 2
      ? formatGameStatus(liveGameState, game.sport)
      : null;
  // Keep the expanded group reachable without re-introducing the jarring
  // page shift this code used to have (full auto-scroll was removed for
  // that). The away ladder inserts ~7 rows ABOVE the clicked row, so a
  // click low in the viewport pushed the row + entire home ladder below
  // the fold and the book looked like it failed to open (Prof's Loom,
  // Jun 12). block:'nearest' is the compromise: zero motion when the
  // group already fits, minimal nudge when it doesn't. rAF so the
  // freshly inserted ladder rows are laid out before measuring.
  const groupTopRef = useRef<HTMLTableRowElement | null>(null);
  const isExpandedNow = expandedTeamSide !== null;
  useEffect(() => {
    if (!isExpandedNow) return;
    const raf = requestAnimationFrame(() => {
      groupTopRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(raf);
  }, [isExpandedNow]);
  const renderTeamRow = (teamSide: TeamSide, isFirstRow: boolean) => {
    const teamName = teamSide === 'away' ? game.awayName : game.homeName;
    const kBook = teamSide === 'away' ? game.kalshi?.away : game.kalshi?.home;
    const pBook = teamSide === 'away' ? game.polymarket?.away : game.polymarket?.home;

    // Pill prices — what number each Y/N pill displays. Driven by the toolbar
    // PriceMode (maker = best bid, taker = best ask, market = last trade).
    const yesAskCents = pillPriceCents(kBook, 'yes', priceMode);
    const noAskCents = pillPriceCents(kBook, 'no', priceMode);
    const polyYesAskCents = pillPriceCents(pBook, 'yes', priceMode);
    const polyNoAskCents = pillPriceCents(pBook, 'no', priceMode);

    // Game-state-derived row treatment. Final rows get a FINAL chip + winner
    // ✓ in place of price pills + dimmed opacity (prices are guaranteed
    // 100¢/0¢, so the pills are noise). Live rows get a small green pulse
    // dot next to the date. Pre rows render normally.
    const isFinal = game.gameState === 'final';
    const isLive = game.gameState === 'live';
    const isWinner = isFinal && game.finalWinnerSide === teamSide;
    const kDepthDelta = depthVelocityDelta(kBook, marketSide);
    const pDepthDelta = depthVelocityDelta(pBook, marketSide);
    const velocityAlert =
      !isFinal && velocityMagnitude(kDepthDelta, pDepthDelta) >= VELOCITY_ALERT_DELTA;

    // Thick top border between games (not on first game, not when this game is expanded — ladder gets it)
    // groupSelected counts: a 3-way group with ANY outcome expanded renders
    // every row in the group as selected (tint + blue rails), so the box
    // wraps the whole market.
    const isGameSelected = expandedTeamSide !== null || groupSelected;
    const borderClass = isFirstRow && gameIndex > 0 && !isGameSelected ? 'border-t-2 border-border' : '';

    // Both rows of the selected game get a darker tint to frame the paired
    // ladders around them; falls back to standard zebra striping when no game is
    // selected. Theme-split via Tailwind utilities — light mode wants a
    // much lighter overlay than the dark-mode value, otherwise rows go
    // near-black against a white page.
    const rowStyle =
      !isGameSelected && gameIndex % 2 === 1
        ? { backgroundColor: 'hsl(var(--muted) / var(--zebra-opacity))' }
        : undefined;

    const isThisRowExpanded = expandedTeamSide === teamSide;

    // ── Consensus odds + score (shared by the Score and Odds columns) ──
    // consensus.awayTeam may land in the row's homeCode slot under the
    // alpha-sorted canonical key, so swap when it does.
    const consensus = liveConsensus;
    const consensusSwapped = consensus != null && consensus.awayTeam === game.homeCode;
    // Draw rows read the 3-way draw prob directly — there's no away/home
    // slot to swap (it's side-less).
    const teamFairCents =
      oddsOutcome === 'draw'
        ? consensus?.drawFairProbCents ?? null
        : consensus
          ? teamSide === 'away'
            ? consensusSwapped
              ? consensus.homeFairProbCents
              : consensus.awayFairProbCents
            : consensusSwapped
              ? consensus.awayFairProbCents
              : consensus.homeFairProbCents
          : null;
    const pctValue = teamFairCents != null ? Math.round(teamFairCents) : null;
    const pct = pctValue != null ? `${pctValue}%` : null;
    // Rank-based tone across the game's outcome set (Dan, Jun 11): the old
    // fixed thresholds (>=51 green / ==50 blue) stop making sense when a
    // soccer 3-way splits 100 three ways and 48 can lead. Highest = green,
    // lowest = blue (title convention), middle/ties = grey — computed over
    // rounded display values so equal-looking numbers color as ties.
    const consensusValues =
      consensus == null
        ? []
        : [
            Math.round(consensus.awayFairProbCents),
            Math.round(consensus.homeFairProbCents),
            ...(consensus.drawFairProbCents != null ? [Math.round(consensus.drawFairProbCents)] : []),
          ];
    const pctTone =
      pctValue == null ? 'text-muted-foreground' : consensusRankTone(pctValue, consensusValues);
    const teamScore =
      oddsOutcome === 'draw' ? null : teamSide === 'away' ? awayScoreDisplay : homeScoreDisplay;
    // Scoreless statusId-3 records must NOT blank the Odds cell — on the
    // 2026-06-12 live soccer game the consensus rendered for a moment on
    // refresh, then vanished when the gameStates poll delivered a garbage
    // FINAL. isConfirmedFinal requires the score.
    const isFinalState = isConfirmedFinal(liveGameState);
    // Brightness conveys confidence: a 50% coin-flip is dimmest, a near-
    // certain call (toward 0/100%) brightest; floored at 0.4 for legibility.
    const confidenceOpacity =
      pctValue == null ? 1 : 0.4 + 0.6 * (Math.abs(pctValue - 50) / 50);
    const consensusAgeMs = consensus != null ? Date.now() - consensus.lastUpdateMs : null;
    const consensusAgeChip =
      consensusAgeMs != null && consensusAgeMs >= 300_000
        ? getTimeAgo(new Date(Date.now() - consensusAgeMs))
        : null;
    const consensusTitle = consensus
      ? `Unabated consensus · ${consensus.contributingBooks} book${consensus.contributingBooks === 1 ? '' : 's'} · ${consensusAgeChip ?? 'just updated'}`
      : undefined;

    return (
      <tr
        key={`${game.key}-${teamSide}`}
        ref={isFirstRow ? groupTopRef : undefined}
        className={cn(
          // Hover matches the selected-row tint so it's clear the row is
          // a click target (was bg-accent/50 which blended into the
          // zebra alternation). The `!` important prefix is load-bearing:
          // alternate rows set `background-color` via an inline `style`,
          // and inline styles outrank plain Tailwind utility classes —
          // including the hover variant. Forcing !important on hover
          // makes the highlight win on zebra rows too.
          'hover:!bg-black/15 dark:hover:!bg-black/95 cursor-pointer transition-colors',
          borderClass,
          // A shade lighter than the ladder rows (bg-black/15 dark:bg-black/95)
          // so the matchup rows read as the focal point of the selected game.
          isGameSelected && 'bg-black/5 dark:bg-black/80',
          // Blue selection box — side rails on the team rows; top/bottom
          // rails live on the first/last ladder rows that bracket them.
          // Suppressed on single-game surfaces (GameDetail) where it's noise.
          isGameSelected && !hideSelectionBox && 'border-blue-500 border-l-2 border-r-2',
          // Single-side rows (period spread/total) have no ladder above
          // them, so the team row IS the box top — give it the top rail.
          // Continuation rows (Draw/Tie) never open the box; the group's
          // top rail lives on the pair above.
          isGameSelected && !hideSelectionBox && singleSide && !continuation && 'border-t-2',
          // Collapsed pair whose Draw/Tie below is the expanded outcome: no
          // ladder renders above it, so the away row is the box top.
          isGameSelected && !hideSelectionBox && !singleSide && expandedTeamSide === null && isFirstRow && 'border-t-2',
          // Collapsed continuation row (its ladder only renders when it is
          // itself the expanded outcome) is the group's last row — it
          // closes the box with the bottom rail.
          isGameSelected && !hideSelectionBox && continuation && expandedTeamSide === null && 'border-b-2',
          velocityAlert && 'animate-velocity-alert-row',
          (isFinal || settled) && 'opacity-60',
        )}
        style={rowStyle}
        onClick={() => onToggleExpand(game.key, teamSide)}
      >
        {/* Date (first row only). Series-winner rows repurpose the column
            to a badge; FINAL rows show a FINAL chip in the same slot
            (guaranteed 5-min lifetime before hydra prunes); per-day rows
            show the date. Event start time renders below the date in a
            smaller line when known; LIVE rows get a green pulse dot
            beside it. Skipped entirely when `hideDateColumn` so the
            consuming table's column-count stays consistent. */}
        {!hideDateColumn && (
          <Td className="text-muted-foreground font-mono text-sm">
            {isFirstRow ? (
              (() => {
                // Series rows carry a fabricated sort-to-bottom date
                // ("9999-<round>-<yy>", marketStore sentinel) and are keyed
                // by canonicalSeriesKey, so no /game/:date/:a/:b page can
                // ever resolve for them — render the badge unlinked instead
                // of a dead link ("Bad game URL" error, Brendan Jun 6).
                // Soccer rows DO link: GameDetail resolves multi-outcome
                // games via getMultiOutcomeEvents since 2026-06-11.
                const isSeriesRow = game.series != null || game.date.startsWith('9999');
                const body = (
                  <>
                    <span className="flex items-center gap-1">
                      {!isLive && !isFinal && game.startTimeMs != null && (
                        <NearEventStartPulse startTimeMs={game.startTimeMs} />
                      )}
                      {game.series ? (
                        <span
                          className="text-foreground inline-block rounded border border-amber-500/60 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-400"
                          title={`Playoff series — ${roundLabel(game.sport, game.series.round)}, 20${game.series.year}`}
                        >
                          {roundLabel(game.sport, game.series.round)}
                        </span>
                      ) : isFinal ? (
                        <span
                          className="inline-block rounded border border-zinc-500/60 bg-zinc-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-400"
                          title="Game finalized — resolved 100¢ / 0¢"
                        >
                          FINAL
                        </span>
                      ) : isLive ? (
                        <LiveBadge />
                      ) : (
                        formatGameDateLabel(game.date)
                      )}
                    </span>
                    {/* Live period status (e.g. "Bot 3rd"). The scheduled start
                        time + countdown moved to the Score column. */}
                    {!isFinal && isLive && liveStatusText && (
                      <span className="text-foreground font-mono text-[16px] font-bold">
                        {liveStatusText}
                      </span>
                    )}
                    {/* Game volume under the date — most-repeated trader ask
                        (Prof Jun 2, Joshua's Jun 8 call notes). Kalshi away +
                        home are distinct tickers (sum); Poly away/home share
                        one slug-level volume (count once). */}
                    {(() => {
                      const gameVol =
                        (game.kalshi?.away.volume ?? 0) +
                        (game.kalshi?.home.volume ?? 0) +
                        (game.polymarket?.away.volume ?? 0);
                      // Always render the line box so the row height doesn't
                      // jump when the first contract trades (>=1 boundary).
                      return (
                        <span
                          className="text-muted-foreground font-mono text-[10px]"
                          title={Math.round(gameVol) >= 1 ? 'Traded volume (Kalshi + Polymarket)' : undefined}
                        >
                          {Math.round(gameVol) >= 1 ? `${formatVolume(gameVol)} vol` : '\u00A0'}
                        </span>
                      );
                    })()}
                  </>
                );
                return isSeriesRow ? (
                  <span className="flex flex-col leading-tight">{body}</span>
                ) : (
                  <Link
                    to={`/game/${game.date}/${game.awayCode}/${game.homeCode}`}
                   
                    onClick={(e) => e.stopPropagation()}
                    className="hover:text-foreground hover:underline focus:outline-none focus-visible:underline flex flex-col leading-tight"
                    title="Open game detail"
                  >
                    {body}
                  </Link>
                );
              })()
            ) : ''}
          </Td>
        )}

        {/* Team — name plus inline per-user POS chips (contracts/open
            orders). The team's consensus odds land in the Odds column to
            the right. */}
        <Td className="font-medium">
          <div className="flex items-center gap-2 min-w-0">
            {/* max-w + truncate keeps long competitor names (tennis player
                full names from yes_sub_title, e.g. "Felix Auger-Aliassime")
                from blowing out the column — the widest Team cell sizes the
                whole column under auto table-layout. Full name on hover via
                title. */}
            {isThisRowExpanded ? (
              <span
                title={teamName}
                className={cn(
                  'inline-block truncate rounded-md border px-2 py-0.5',
                  labelMaxWidthClass,
                  marketSide === 'yes'
                    ? 'border-positive bg-positive/10 text-positive'
                    : 'border-negative bg-negative/10 text-negative',
                )}
              >
                {teamName}
              </span>
            ) : (
              <span title={teamName} className={cn(labelMaxWidthClass, 'truncate')}>
                {teamName}
              </span>
            )}
            {/* SETTLED chip — first row only (singleSide rows are their own
                first row, so period bets get exactly one). Same visual
                vocabulary as the Date column's FINAL chip, which these
                surfaces hide. */}
            {settled && isFirstRow && (
              <span
                className="inline-block shrink-0 rounded border border-zinc-500/60 bg-zinc-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-400"
                title="Market resolved — prices are settlement values, not tradable quotes"
              >
                SETTLED
              </span>
            )}
            <PosCell
              ticker={
                teamSide === 'away' ? game.kalshi?.away.marketTicker : game.kalshi?.home.marketTicker
              }
              book={teamSide === 'away' ? game.kalshi?.away ?? null : game.kalshi?.home ?? null}
              positionByTicker={positionByTicker}
              ordersByTicker={ordersByTicker}
            />
          </div>
        </Td>

        {/* Score — kickoff time + countdown until the game starts, then the
            live/final score. Score is per-team (each row shows its side); the
            pre-game time line is per-game, so it renders on the first row
            only. */}
        {!hideScoreColumn && (
          <Td className="whitespace-nowrap text-center font-mono">
            {showLiveScore && teamScore != null ? (
              <span className="text-foreground text-[16px] font-bold">{teamScore}</span>
            ) : isLive ? (
              // Live but no score feed (tennis & other un-scored sports):
              // an explicit dash, never the stale start time (Joshua, Jun 6).
              <span className="text-muted-foreground/40">—</span>
            ) : !isFinal && isFirstRow && game.timePt ? (
              <span className="flex flex-col items-center leading-tight">
                <span className="text-foreground text-[16px] font-bold">{game.timePt}</span>
                {game.startTimeMs != null && (
                  <GameCountdown startTimeMs={game.startTimeMs} className="text-sm" />
                )}
              </span>
            ) : (
              ''
            )}
          </Td>
        )}

        {/* Odds — Unabated consensus win probability for this team, in its
            own column (split out of the old Score/Odds cell). Brightness
            conveys confidence: a 50/50 coin-flip is dimmest, a near-certain
            call (toward 0/100%) brightest. Blank once the game is final. */}
        {!hideOddsColumn && (
          <Td className="whitespace-nowrap text-center font-mono tabular-nums">
            {pct != null && pctValue != null && !isFinalState ? (
              <OddsPctText
                pctValue={pctValue}
                text={pct}
                toneClass={pctTone}
                sizeClass="text-[17px]"
                opacity={confidenceOpacity}
                title={consensusTitle}
              />
            ) : (
              <span className="text-muted-foreground/40">—</span>
            )}
          </Td>
        )}

        {/* Kalshi Yes/No pills — replaced by winner ✓ / blank on FINAL rows
            since 100¢/0¢ pills are noise once the market resolves. */}
        <Td className="whitespace-nowrap text-center">
          {isFinal ? (
            <div className="flex items-center justify-center text-sm">
              {isWinner ? (
                <span className="text-positive font-semibold" title="Resolved YES (winner)">✓</span>
              ) : (
                <span className="text-muted-foreground/40">—</span>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center gap-1.5">
              <PricePill
                label="Y"
                cents={yesAskCents}
                side="yes"
                activeMarketSide={marketSide}
                priceMode={priceMode}
                disabled={kBook == null}
                ticker={kBook?.marketTicker ?? null}
                makerRebatePct={makerRebateFraction}
                onClick={(e) => {
                  e.stopPropagation();
                  if (yesAskCents == null) return;
                  onTradeClick?.(game.key, teamSide, 'kalshi', {
                    priceCents: yesAskCents,
                    initialOrderSide: 'yes',
                    postOnly: false,
                  });
                }}
              />
              <PricePill
                label="N"
                cents={noAskCents}
                side="no"
                activeMarketSide={marketSide}
                priceMode={priceMode}
                disabled={kBook == null}
                ticker={kBook?.marketTicker ?? null}
                makerRebatePct={makerRebateFraction}
                onClick={(e) => {
                  e.stopPropagation();
                  if (noAskCents == null) return;
                  onTradeClick?.(game.key, teamSide, 'kalshi', {
                    priceCents: noAskCents,
                    initialOrderSide: 'no',
                    postOnly: false,
                  });
                }}
              />
            </div>
          )}
        </Td>

        {/* K Volume */}
        <Td className="text-muted-foreground text-right font-mono text-sm">
          {isFinal ? (
            ''
          ) : (
            <span key={kBook?.volume} className="animate-flash-neutral inline-block">
              {kBook ? formatVolume(kBook.volume) : '—'}
            </span>
          )}
        </Td>

        {/* K Liquidity — total $ in book; ladder pills scale against this */}
        <Td className="text-muted-foreground text-right font-mono text-sm">
          {isFinal ? '' : kBook ? formatCompactCurrency(kBook.liquidity) : '—'}
        </Td>

        {/* K Queue — contracts resting at the best bid on the active marketSide.
            Color follows the same absolute thresholds the trade-view cards use
            (queueTone in orderCardModel.ts): <50k green, 50-300k yellow, ≥300k
            red. Returns null (→ '—') when the book has no real bid on that
            side. FINAL rows blank since the resolved book carries no signal. */}
        {(() => {
          const kQueue = bestBidDepthForSide(kBook, marketSide);
          return (
            <Td className={cn('text-right font-mono text-sm', queueTone(kQueue, null))}>
              {isFinal ? '' : kQueue == null ? '—' : formatVolume(kQueue)}
            </Td>
          );
        })()}

        {/* K Δ/m — best-bid depth rate of change over the trailing 1 minute on
            the active marketSide. Pair-reads with the P Δ/m cell to the right
            of the Poly column for cross-exchange sweep detection. Empty until
            ~1m of samples accumulates. */}
        <Td className={cn('text-right font-mono text-sm', depthDeltaTone(kDepthDelta))}>
          {isFinal ? '' : kDepthDelta == null ? '—' : formatSignedVolume(kDepthDelta)}
        </Td>

        {/* Polymarket Yes/No pills — same pattern as Kalshi, FINAL same swap */}
        <Td className="whitespace-nowrap text-center">
          {isFinal ? (
            <div className="flex items-center justify-center text-sm">
              {isWinner ? (
                <span className="text-positive font-semibold" title="Resolved YES (winner)">✓</span>
              ) : (
                <span className="text-muted-foreground/40">—</span>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center gap-1.5">
              <PricePill
                label="Y"
                cents={polyYesAskCents}
                side="yes"
                activeMarketSide={marketSide}
                priceMode={priceMode}
                disabled={pBook == null}
                ticker={pBook?.marketTicker ?? null}
                makerRebatePct={makerRebateFraction}
                onClick={(e) => {
                  e.stopPropagation();
                  onTradeClick?.(game.key, teamSide, 'polymarket', {
                    priceCents: polyYesAskCents ?? undefined,
                    initialOrderSide: 'yes',
                    postOnly: false,
                  });
                }}
              />
              <PricePill
                label="N"
                cents={polyNoAskCents}
                side="no"
                activeMarketSide={marketSide}
                priceMode={priceMode}
                disabled={pBook == null}
                ticker={pBook?.marketTicker ?? null}
                makerRebatePct={makerRebateFraction}
                onClick={(e) => {
                  e.stopPropagation();
                  onTradeClick?.(game.key, teamSide, 'polymarket', {
                    priceCents: polyNoAskCents ?? undefined,
                    initialOrderSide: 'no',
                    postOnly: false,
                  });
                }}
              />
            </div>
          )}
        </Td>

        {/* P Volume */}
        <Td className="text-muted-foreground text-right font-mono text-sm">
          {isFinal ? (
            ''
          ) : (
            <span key={pBook?.volume} className="animate-flash-neutral inline-block">
              {pBook ? formatVolume(pBook.volume) : '—'}
            </span>
          )}
        </Td>

        {/* P Liquidity — total $ in book; ladder pills scale against this */}
        <Td className="text-muted-foreground text-right font-mono text-sm">
          {isFinal ? '' : pBook ? formatCompactCurrency(pBook.liquidity) : '—'}
        </Td>

        {/* P Δ/m — Polymarket sibling of K Δ/m. Same active-side semantic. */}
        <Td className={cn('text-right font-mono text-sm', depthDeltaTone(pDepthDelta))}>
          {isFinal ? '' : pDepthDelta == null ? '—' : formatSignedVolume(pDepthDelta)}
        </Td>
      </tr>
    );
  };

  // 3 ask rows + 3 bid rows for a single team. Whenever either team is
  // selected, render both team books around the paired matchup rows: away book
  // above the two names, home book below them. This keeps the existing
  // full-width ladder UX while making the opposite side visible immediately.
  // YES side: asks come from inverting NO bids (100 − no[i].price), bids come from yes[].
  // NO  side: asks come from inverting YES bids, bids come from no[].
  const renderLadderRows = (teamSide: TeamSide) => {
    const teamCode = teamSide === 'away' ? game.awayCode : game.homeCode;
    const teamName = teamSide === 'away' ? game.awayName : game.homeName;
    const kBook = teamSide === 'away' ? game.kalshi?.away : game.kalshi?.home;
    const pBook = teamSide === 'away' ? game.polymarket?.away : game.polymarket?.home;
    if (!kBook && !pBook) return null;

    const buildSide = (book: VenueBook | null | undefined) => {
      if (!book) return { asks: [] as BookLevel[], bids: [] as BookLevel[] };
      const ownBids = marketSide === 'yes' ? book.yes : book.no;
      const oppositeBids = marketSide === 'yes' ? book.no : book.yes;
      const lastYes = book.lastPriceCents;
      const ownLast = lastYes == null ? null : marketSide === 'yes' ? lastYes : 100 - lastYes;
      const oppositeLast = lastYes == null ? null : marketSide === 'yes' ? 100 - lastYes : lastYes;
      return {
        asks: oppositeBids
          .filter(bidIsReal(oppositeLast))
          .slice(0, LADDER_LEVELS)
          .map((l) => ({ priceCents: 100 - l.priceCents, size: l.size })),
        bids: ownBids.filter(bidIsReal(ownLast)).slice(0, LADDER_LEVELS),
      };
    };

    const k = buildSide(kBook);
    const p = buildSide(pBook);

    // Scale anchor for ladder liquidity pills: % of total venue book liquidity.
    const kMaxLiq = kBook?.liquidity && kBook.liquidity > 0 ? kBook.liquidity : 1;
    const pMaxLiq = pBook?.liquidity && pBook.liquidity > 0 ? pBook.liquidity : 1;

    // Per-price-level user-order chips for the Kalshi ladder. Split by action
    // because BUY orders rest on the bid stack and need to render alongside
    // bid rows, while SELL orders rest on the ask stack and need to render
    // alongside ask rows. Filtered to marketSide (the side currently expanded)
    // and sorted within each price level by qty desc so the largest order
    // shows first.
    const buildOrdersByCents = (action: 'buy' | 'sell') => {
      const map = new Map<number, BidRowOrder[]>();
      if (!kBook) return map;
      const orders = ordersByTicker.get(kBook.marketTicker) ?? [];
      for (const o of orders) {
        if (o.action !== action) continue;
        if (o.side !== marketSide) continue;
        const depth = queueDepth(o, kBook);
        const list = map.get(o.priceCents) ?? [];
        list.push({
          orderId: o.orderId,
          remainingCount: o.remainingCount,
          queueOrBetter: depth.atLevelOrBetter,
          tooltip: orderTooltip(o, kBook),
        });
        map.set(o.priceCents, list);
      }
      for (const list of map.values()) {
        list.sort((a, b) => b.remainingCount - a.remainingCount);
      }
      return map;
    };
    const kBuyOrdersByCents = buildOrdersByCents('buy');
    const kSellOrdersByCents = buildOrdersByCents('sell');

    // Visual order: asks highest→lowest (worst at top, best at bottom near strip), then strip,
    // then bids highest→lowest (best at top near strip, worst at bottom).
    const kAsksVisual = [...k.asks].reverse();
    const kBidsVisual = k.bids;
    const pAsksVisual = [...p.asks].reverse();
    const pBidsVisual = p.bids;

    // hydra reports last trade as the YES last; invert for NO side.
    // Render a separate strip price per venue so K and P can diverge.
    const lastFor = (book: VenueBook | null | undefined): number | null => {
      const lastYes = book?.lastPriceCents ?? null;
      if (lastYes == null) return null;
      return marketSide === 'yes' ? lastYes : 100 - lastYes;
    };
    const kLast = lastFor(kBook);
    const pLast = lastFor(pBook);
    const padTo = (rows: BookLevel[]): (BookLevel | null)[] => {
      const out: (BookLevel | null)[] = [...rows];
      while (out.length < LADDER_LEVELS) out.push(null);
      return out;
    };

    const kAsksPadded = padTo(kAsksVisual);
    const kBidsPadded = padTo(kBidsVisual);
    const pAsksPadded = padTo(pAsksVisual);
    const pBidsPadded = padTo(pBidsVisual);

    const renderBookRows = (
      kind: 'ask' | 'bid',
      kRows: Array<BookLevel | null>,
      pRows: Array<BookLevel | null>,
    ) => {
      return Array.from({ length: LADDER_LEVELS }, (_, idx) => {
        const kLevel = kRows[idx] ?? null;
        const pLevel = pRows[idx] ?? null;
        // The group renders away-ladder → team rows → home-ladder, so the
        // very first row is the away asks' top and the very last is the home
        // bids' bottom. Those two get the box's horizontal rails; the side
        // rails + selected-team tint apply to every ladder row so the book
        // reads as part of the highlighted game.
        // In the binary pair the away-ask top and home-bid bottom bracket the
        // team rows. A single-side row has no ladder above it (the team row is
        // the box top, handled there), so its one ladder only carries the
        // bottom rail on its last bid row.
        const isGroupTop = !singleSide && teamSide === 'away' && kind === 'ask' && idx === 0;
        // No bottom rail when a Draw/Tie continuation row follows — that row
        // (or its own ladder) closes the box instead.
        const isGroupBottom =
          !groupExtendsBelow &&
          (singleSide
            ? kind === 'bid' && idx === LADDER_LEVELS - 1
            : teamSide === 'home' && kind === 'bid' && idx === LADDER_LEVELS - 1);
        const rowClass = cn(
          'bg-black/15 dark:bg-black/95',
          // Blue selection box rails — suppressed on single-game surfaces
          // (GameDetail); the selected-team tint stays either way.
          !hideSelectionBox && 'border-blue-500 border-l-2 border-r-2',
          !hideSelectionBox && isGroupTop && 'border-t-2',
          !hideSelectionBox && isGroupBottom && 'border-b-2',
        );
        return (
          <LadderRow
            key={`${game.key}-${teamSide}-${kind}-${idx}`}
            rowClass={rowClass}
            kLevel={kLevel}
            pLevel={pLevel}
            kTicker={kBook?.marketTicker}
            pTicker={pBook?.marketTicker}
            kMaxLiq={kMaxLiq}
            pMaxLiq={pMaxLiq}
            kLastCents={kLast}
            pLastCents={pLast}
            kind={kind}
            marketSide={marketSide}
            priceMode={priceMode}
            makerRebatePct={makerRebateFraction}
            teamCode={teamCode}
            teamName={teamName}
            isSelectedTeam={expandedTeamSide === teamSide}
            kUserOrders={
              kLevel
                ? kind === 'ask'
                  ? kSellOrdersByCents.get(kLevel.priceCents)
                  : kBuyOrdersByCents.get(kLevel.priceCents)
                : undefined
            }
            onKalshiClick={() => {
              if (!kLevel) return;
              onTradeClick?.(game.key, teamSide, 'kalshi', {
                priceCents: kLevel.priceCents,
                initialOrderSide: marketSide,
                postOnly: kind === 'bid',
              });
            }}
            onPolyClick={() => {
              if (!pLevel) return;
              onTradeClick?.(game.key, teamSide, 'polymarket', {
                priceCents: pLevel.priceCents,
                initialOrderSide: marketSide,
                postOnly: kind === 'bid',
              });
            }}
            hideDateColumn={hideDateColumn}
            hideScoreColumn={hideScoreColumn}
            hideOddsColumn={hideOddsColumn}
          />
        );
      });
    };

    return (
      <Fragment key={`${game.key}-${teamSide}-ladder`}>
        {renderBookRows('ask', kAsksPadded, pAsksPadded)}
        {/* Asks ↔ bids separator. Faint thin grey hairline so it's
            clear where the spread sits without competing visually
            with the row borders. */}
        <tr
          aria-hidden="true"
          className={cn(
            'bg-black/15 dark:bg-black/95',
            !hideSelectionBox && 'border-blue-500 border-l-2 border-r-2',
          )}
        >
          <td colSpan={13 - (hideDateColumn ? 1 : 0) - (hideScoreColumn ? 1 : 0) - (hideOddsColumn ? 1 : 0)} className="border-border/40 border-t p-0" />
        </tr>
        {renderBookRows('bid', kBidsPadded, pBidsPadded)}
      </Fragment>
    );
  };

  const showPairedBooks = expandedTeamSide !== null;

  // Single-outcome mode (Game Lines: Tie, single spread/total lines): render
  // just this outcome's row + its own ladder, not the away/home pair.
  if (singleSide) {
    return (
      <Fragment>
        {renderTeamRow(singleSide, !continuation)}
        {showPairedBooks && renderLadderRows(singleSide)}
      </Fragment>
    );
  }

  return (
    <Fragment>
      {showPairedBooks && renderLadderRows('away')}
      {renderTeamRow('away', true)}
      {renderTeamRow('home', false)}
      {showPairedBooks && renderLadderRows('home')}
    </Fragment>
  );
}

// ── PosCell — per-user Kalshi position + open-order chips ───────────
// Renders side-by-side chips for the row's Kalshi ticker, scoped to the
// logged-in user. The position chip ('c') uses an SVG ring split into a
// green YES arc and a red NO arc proportional to the user's net contracts
// per side, so a 75/25 holding shows 75% green + 25% red. Each open order
// gets its own ('o') chip with `queue_position / queue_position + better`
// — "better" means cumulative same-side bid depth at strictly more
// aggressive prices, recomputed from the current orderbook on every render
// (no extra subscription; the row already updates with marketStore RAF).

function PosCell({
  ticker,
  book,
  positionByTicker,
  ordersByTicker,
}: {
  ticker: string | null | undefined;
  book: VenueBook | null;
  positionByTicker: Map<string, UserPositionSlice>;
  ordersByTicker: Map<string, UserOrderSlice[]>;
}) {
  if (!ticker) return null;
  const pos = positionByTicker.get(ticker);
  const orders = ordersByTicker.get(ticker);
  if (!pos && (!orders || orders.length === 0)) return null;

  return (
    <div className="flex items-center gap-1">
      {pos && pos.yesCount + pos.noCount > 0 && (
        <span
          className="inline-flex items-center"
          title={
            pos.yesCount > 0 && pos.noCount > 0
              ? `${pos.yesCount} YES, ${pos.noCount} NO`
              : pos.yesCount > 0
                ? `${pos.yesCount} YES contract${pos.yesCount === 1 ? '' : 's'}`
                : `${pos.noCount} NO contract${pos.noCount === 1 ? '' : 's'}`
          }
        >
          <PosRing yesCount={pos.yesCount} noCount={pos.noCount} variant="closed" size={20} />
        </span>
      )}
      {/* Open-order indicator — single O chip aggregating all working orders
          on this ticker (per-row qty/queue text already lives on the ladder
          rows). Ring colors split by yes/no order count so a mixed book reads
          at a glance; tooltip lists each order one per line. */}
      {orders && orders.length > 0 && (() => {
        let yesOrders = 0;
        let noOrders = 0;
        for (const o of orders) {
          if (o.side === 'yes') yesOrders += 1;
          else noOrders += 1;
        }
        const tooltip = orders.map((o) => orderTooltip(o, book)).join('\n');
        return (
          <span className="inline-flex items-center" title={tooltip}>
            <PosRing yesCount={yesOrders} noCount={noOrders} variant="open" size={20} />
          </span>
        );
      })()}
    </div>
  );
}

// ── OddsPctText — consensus % that pulses + shows a direction arrow on change ──
// Color and confidence-opacity are decided by the caller; this wrapper owns
// the change cue so the flash timer lives per cell (one instance per team
// row). useFlashOnChange(trackDirection) gives us both the 10s on/off window
// and the up/down sense: while it's live the number wears the amber
// `animate-odds-change-pulse` glow and a green ▲ (rose) / red ▼ (fell) arrow
// shows beside it. Keyed on the rounded pctValue so sub-percent jitter
// doesn't trigger a pulse. The arrow ignores the confidence-opacity dim so
// the direction stays legible even on a near-coin-flip.
function OddsPctText({
  pctValue,
  text,
  toneClass,
  sizeClass,
  opacity,
  title,
}: {
  pctValue: number;
  text: string;
  toneClass: string;
  sizeClass: string;
  opacity: number;
  title?: string;
}) {
  const flashClass = useFlashOnChange(pctValue, { duration: 10_000, trackDirection: true });
  const isPulsing = flashClass !== '';
  const direction =
    flashClass === FLASH_CLASS.up ? 'up' : flashClass === FLASH_CLASS.down ? 'down' : null;
  return (
    <span
      className={cn('inline-flex items-center gap-0.5', isPulsing && 'animate-odds-change-pulse')}
    >
      {direction && (
        <span
          aria-hidden
          className={cn(
            'leading-none',
            sizeClass,
            direction === 'up' ? 'text-positive' : 'text-negative',
          )}
        >
          {direction === 'up' ? '▲' : '▼'}
        </span>
      )}
      <span className={cn(sizeClass, toneClass)} style={{ opacity }} title={title}>
        {text}
      </span>
    </span>
  );
}

// ── LadderRow ────────────────────────────────────────────────────────

function LadderRow({
  kLevel,
  pLevel,
  kTicker,
  pTicker,
  kMaxLiq,
  pMaxLiq,
  kLastCents,
  pLastCents,
  kind,
  marketSide,
  priceMode,
  makerRebatePct = 0,
  teamCode,
  teamName,
  isSelectedTeam,
  kUserOrders,
  onKalshiClick,
  onPolyClick,
  rowClass,
  hideDateColumn = false,
  hideScoreColumn = false,
  hideOddsColumn = false,
}: {
  kLevel: BookLevel | null;
  pLevel: BookLevel | null;
  kTicker?: string | null;
  pTicker?: string | null;
  kMaxLiq: number;
  pMaxLiq: number;
  /** The dashboard's active Y/N toggle. The price cells align under the
   *  matching pill — left (under Y) for 'yes', right (under N) for 'no' —
   *  so the ladder visually hangs beneath whichever side is selected. */
  marketSide: MarketSide;
  /** The toolbar's active price mode (maker/taker/market). Drives the fee
   *  perspective for this row's odds — same mapping the Y/N pills use — so
   *  every price on the table reflects the selected perspective rather than
   *  a per-row bid/ask assumption. */
  priceMode: PriceMode;
  /** Maker-fee rebate fraction (0..1) folded into maker-perspective odds;
   *  0 = no rebate. Computed once per game by the parent. */
  makerRebatePct?: number;
  /** Side-native last trade for the Kalshi book (already inverted for
   *  NO when marketSide='no' upstream). Drives the slow-pulse grey
   *  glow on whichever ladder row matches it. Null when unknown. */
  kLastCents?: number | null;
  /** Same for the Polymarket book. */
  pLastCents?: number | null;
  kind: 'ask' | 'bid';
  teamCode: string;
  teamName: string;
  isSelectedTeam: boolean;
  /** User open orders matching this Kalshi bid row's price (bid kind only).
   *  Pre-sorted by qty desc, with same hover tooltip as the inline O ring. */
  kUserOrders?: BidRowOrder[];
  onKalshiClick: () => void;
  onPolyClick: () => void;
  /** Tint + blue-selection-box rails for this ladder row, computed by the
   *  caller from the row's position in the expanded game group. */
  rowClass?: string;
  /** Skip the leading Date spacer when the containing table doesn't
   *  carry a Date column (GameDetail). Matches the GameRows prop. */
  hideDateColumn?: boolean;
  /** Skip the Score-column spacer / Odds (user-orders) column to match a
   *  thead without those columns (GameDetail Game Lines). */
  hideScoreColumn?: boolean;
  hideOddsColumn?: boolean;
}) {
  const kIsLastTrade = kLastCents != null && kLevel?.priceCents === kLastCents;
  const pIsLastTrade = pLastCents != null && pLevel?.priceCents === pLastCents;
  const kLiq = kLevel ? Math.round((kLevel.priceCents * kLevel.size) / 100) : 0;
  const pLiq = pLevel ? Math.round((pLevel.priceCents * pLevel.size) / 100) : 0;
  const colorClass = kind === 'ask' ? 'text-negative' : 'text-positive';
  const hasUserOrders = kUserOrders && kUserOrders.length > 0;

  // Per-row American odds — fee-adjusted via the same formula the order
  // entry uses when the Fees toggle is on (perContractFeeCents, floored at
  // Kalshi's 1¢-per-fill minimum). Polymarket has no fee schedule on file,
  // so its pFeeEntry resolves to null → raw odds.
  //
  // Fee perspective follows the table's price-mode toggle (the same mapping
  // the Y/N pills use), NOT the row's bid/ask kind. A resting order — buy or
  // sell, bid side or ask side — is always a MAKER, so the old
  // `ask ⇒ taker` split mis-priced ask rows by ~4× on maker-fee series and
  // disagreed with the maker-priced order ticket a row click opens. The
  // trader chooses the perspective they care about via Maker/Taker/Market.
  const feeAdjusted = useDisplaySettings((s) => s.feeAdjustedOdds);
  const kFeeEntry = useFeeFor(kTicker);
  const pFeeEntry = useFeeFor(pTicker);
  const oddsPerspective: FeePerspective = priceMode === 'maker' ? 'maker' : 'taker';
  const kEffectiveCents = kLevel
    ? effectivePriceForOdds(kLevel.priceCents, feeAdjusted, kFeeEntry, oddsPerspective, makerRebatePct)
    : null;
  const pEffectiveCents = pLevel
    ? effectivePriceForOdds(pLevel.priceCents, feeAdjusted, pFeeEntry, oddsPerspective, makerRebatePct)
    : null;
  const kOdds = kEffectiveCents != null ? americanOdds(kEffectiveCents) : null;
  const pOdds = pEffectiveCents != null ? americanOdds(pEffectiveCents) : null;

  // Price + odds nodes, defined once and dropped into the active half of the
  // split price cell below (see the two-half alignment note there).
  const kPriceOdds = kLevel ? (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-1',
        kIsLastTrade && 'animate-last-trade-pulse',
      )}
    >
      <span
        className={cn(
          'inline-block w-[4ch] text-right tabular-nums cursor-pointer hover:underline',
          colorClass,
        )}
        onClick={(e) => {
          e.stopPropagation();
          onKalshiClick();
        }}
        title={kIsLastTrade ? `Last trade @ ${kLevel.priceCents}¢` : 'Click to trade'}
      >
        {kLevel.priceCents}¢
      </span>
      {kOdds && (
        <>
          <span className="text-muted-foreground/40 text-xs">|</span>
          <span className="text-foreground text-xs">{kOdds}</span>
        </>
      )}
    </span>
  ) : null;
  const pPriceOdds = pLevel ? (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-1',
        pIsLastTrade && 'animate-last-trade-pulse',
      )}
    >
      <span
        className={cn(
          'inline-block w-[4ch] text-right tabular-nums cursor-pointer hover:underline',
          colorClass,
        )}
        onClick={(e) => {
          e.stopPropagation();
          onPolyClick();
        }}
        title={pIsLastTrade ? `Last trade @ ${pLevel.priceCents}¢` : 'Click to trade'}
      >
        {pLevel.priceCents}¢
      </span>
      {pOdds && (
        <>
          <span className="text-muted-foreground/40 text-xs">|</span>
          <span className="text-foreground text-xs">{pOdds}</span>
        </>
      )}
    </span>
  ) : null;

  return (
    <tr className={cn(rowClass)}>
      {!hideDateColumn && <Td className="py-0.5"> </Td>}
      {/* Compact ladder label so paired books remain readable when both teams open. */}
      <Td className="py-0.5">
        <div
          className="flex min-w-0 items-center gap-1.5 font-mono text-[10px]"
          title={`${teamName} ${kind}`}
        >
          <span
            className={cn(
              'max-w-[5ch] truncate rounded px-1 py-px font-semibold',
              isSelectedTeam ? 'bg-primary/10 text-primary' : 'text-muted-foreground',
            )}
          >
            {teamCode}
          </span>
          <span className={cn('font-semibold', colorClass)}>{kind.toUpperCase()}</span>
        </div>
      </Td>
      {/* Score-column spacer — score/countdown lives in the summary rows. */}
      {!hideScoreColumn && <Td className="py-0.5">{' '}</Td>}

      {/* Your active Kalshi orders resting at this price level (qty / queue-or-
          better, vertical-bar separated, qty desc). This is the column the
          Ask/Bid label used to occupy — direction is already conveyed by the
          price color and which half of the ladder (above/below the spread
          hairline) the row sits in, so the label was redundant. Right-aligned
          so the chips sit flush against the price column. Inert: clicks stop
          here so they don't open the trade modal. (Poly user orders aren't
          tracked yet, so this stays Kalshi-only for now.) */}
      {!hideOddsColumn && (
        <Td className="py-0.5 text-right">
          {hasUserOrders ? (
            <div
              className="text-foreground/90 flex items-center justify-end gap-1.5 overflow-hidden text-[10px]"
              onClick={(e) => e.stopPropagation()}
            >
              {kUserOrders!.map((o, i) => (
                <Fragment key={o.orderId}>
                  {i > 0 && <span className="bg-muted-foreground/40 h-3 w-px shrink-0 self-center" />}
                  {/* qty stays neutral (user's own size); queueOrBetter gets the
                      absolute queue tone so dashboard ladder chips match the
                      trade-view cards' green/yellow/red treatment. */}
                  <span className="truncate" title={o.tooltip}>
                    <span>{Math.round(o.remainingCount)}/</span>
                    <span className={queueTone(o.queueOrBetter, null)}>
                      {o.queueOrBetter != null ? formatVolume(Math.round(o.queueOrBetter)) : '?'}
                    </span>
                  </span>
                </Fragment>
              ))}
            </div>
          ) : (
            ''
          )}
        </Td>
      )}

      {/* Kalshi price + American odds. The cell is split into two equal
          halves so the value hangs under the *active* pill: left half (under
          Y) when marketSide='yes', right half — its left edge at the cell
          midpoint, ≈ under the N pill's left edge — when 'no'. Left-justified
          within the half, so mixed integer / 1-decimal odds line up on their
          left edge regardless of width. */}
      <Td className="whitespace-nowrap py-0.5 font-mono">
        {kLevel ? (
          <div className="flex">
            <div className="w-1/2 text-left">{marketSide === 'yes' ? kPriceOdds : null}</div>
            <div className="w-1/2 text-left">{marketSide === 'no' ? kPriceOdds : null}</div>
          </div>
        ) : null}
      </Td>

      {/* K Size */}
      <Td className="text-muted-foreground py-0.5 text-right font-mono text-xs">
        {kLevel ? formatVolume(kLevel.size) : ''}
      </Td>

      {/* K Liquidity — scaled to % of total Kalshi book liquidity */}
      <Td className="py-0.5">
        <LiquidityBar value={kLiq} maxValue={kMaxLiq} height={16} />
      </Td>

      {/* K Queue spacer — the K Q column lives in the team rows above;
          ladder rows leave it blank to keep header→row column alignment
          intact. The per-level size already lives in the K Size column to
          the left, so a queue read here would be redundant. */}
      <Td className="py-0.5">{' '}</Td>

      {/* K Δ/m spacer — velocity column lives in the team rows above. */}
      <Td className="py-0.5">{' '}</Td>

      {/* Polymarket price — same two-half active-pill alignment as Kalshi. */}
      <Td className="whitespace-nowrap py-0.5 font-mono">
        {pLevel ? (
          <div className="flex">
            <div className="w-1/2 text-left">{marketSide === 'yes' ? pPriceOdds : null}</div>
            <div className="w-1/2 text-left">{marketSide === 'no' ? pPriceOdds : null}</div>
          </div>
        ) : null}
      </Td>

      {/* P Size */}
      <Td className="text-muted-foreground py-0.5 text-right font-mono text-xs">
        {pLevel ? formatVolume(pLevel.size) : ''}
      </Td>

      {/* P Liquidity — scaled to % of total Polymarket book liquidity */}
      <Td className="py-0.5">
        <LiquidityBar value={pLiq} maxValue={pMaxLiq} height={16} />
      </Td>

      {/* P Δ/m spacer — velocity column lives in the team rows above. */}
      <Td className="py-0.5">{' '}</Td>
    </tr>
  );
}
